// ✅ 최종 완성본 DocumentBilling.tsx
// (분할금액 + 출고일 15일 기준 처리 포함)

import React, { useState } from 'react';
import { Box, Typography, FormControl, InputLabel, Select, MenuItem, Grid, Button, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import api from '../api';

const years = ['2024', '2025', '2026'];
const months = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];

const getPlannedMonth = (dateStr?: string) => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  let year = date.getFullYear();
  let month = date.getMonth() + 1;
  const day = date.getDate();
  if (day >= 16) {
    month += 1;
    if (month === 13) {
      month = 1;
      year += 1;
    }
  }
  return `${year}-${String(month).padStart(2, '0')}`;
};

interface Payment {
  id: number;
  amount: number;
  due_date: string;
  status: string;
}

interface SaleItem {
  id: number;
  item: string;
  specification?: string;
  amount: number;
  payments: Payment[];
  company_name: string;
  site_name: string;
  sale_id: number;
  release_date?: string;
  invoice_status?: string;
}

interface ItemRow {
  name: string;
  spec: string;
  unit: string;
  qty: number;
  unitPrice: number;
  totalPrice: number;
  prevQty: number;
  prevAmt: number;
  currQty: number;
  currAmt: number;
  totalQty: number;
  totalAmt: number;
  remainQty: number;
  remainAmt: number;
  remark: string;
}

const DocumentBilling = () => {
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [items, setItems] = useState<SaleItem[]>([]);

  const targetMonth = `${selectedYear}-${selectedMonth}`;

  const handleYearChange = (event: any) => setSelectedYear(event.target.value);
  const handleMonthChange = (event: any) => setSelectedMonth(event.target.value);

  const handleSearch = async () => {
    if (!selectedYear || !selectedMonth) return;
    try {
      const res = await api.get('/api/sales');
      const sales = res.data;
      const fetchedItems: SaleItem[] = [];

      for (const sale of sales) {
        for (const item of sale.items) {
          const hasCurrentMonth = item.payments.some((p: Payment) => {
            const pMonth = getPlannedMonth(p.due_date);
            return pMonth === targetMonth;
          }) || (item.release_date && getPlannedMonth(item.release_date) === targetMonth);

          if (hasCurrentMonth) {
            fetchedItems.push({
              id: item.id,
              item: item.item,
              specification: item.specification || '',
              amount: item.amount,
              payments: item.payments || [],
              company_name: sale.company.name,
              site_name: sale.site.name,
              sale_id: sale.id,
              release_date: item.release_date,
              invoice_status: item.invoice_status
            });
          }
        }
      }
      setItems(fetchedItems);
    } catch (err) {
      console.error('데이터 조회 실패:', err);
    }
  };

  const formatAmount = (amt: number) => amt.toLocaleString() + '원';

  const handleGenerate = async (clickedItem: SaleItem) => {
    try {
      const res = await api.get('/api/sales');
      const sales = res.data;
      const sale = sales.find((s: any) => s.id === clickedItem.sale_id);

      if (!sale) {
        alert('해당 계약서를 찾을 수 없습니다.');
        return;
      }

      const selectedYM = `${selectedYear}-${selectedMonth}`;

      const itemRows: ItemRow[] = sale.items.map((item: any) => {
        const originalAmount = item.amount;

        let prevAmt = 0;
        let currAmt = 0;

        // 분할금 기준
        for (const pay of item.payments || []) {
          const payMonth = getPlannedMonth(pay.due_date);
          if (payMonth) {
            if (payMonth < selectedYM) prevAmt += pay.amount;
            else if (payMonth === selectedYM) currAmt += pay.amount;
          }
        }

        // 출고일 기준
        if (item.release_date) {
          const releaseDate = new Date(item.release_date);
          const day = releaseDate.getDate();
          let releaseYM = `${releaseDate.getFullYear()}-${String(releaseDate.getMonth() + 1).padStart(2, '0')}`;
          if (day > 15) {
            let nextMonth = releaseDate.getMonth() + 2;
            let nextYear = releaseDate.getFullYear();
            if (nextMonth > 12) {
              nextMonth = 1;
              nextYear += 1;
            }
            releaseYM = `${nextYear}-${String(nextMonth).padStart(2, '0')}`;
          }

          if (releaseYM < selectedYM) prevAmt += (originalAmount - prevAmt - currAmt);
          else if (releaseYM === selectedYM) currAmt += (originalAmount - prevAmt - currAmt);
        }

        const totalAmt = prevAmt + currAmt;
        const remainAmt = originalAmount - totalAmt;
        const remark = originalAmount ? `${Math.round((remainAmt / originalAmount) * 10000) / 100}%` : '-';

        return {
          name: item.item,
          spec: item.specification || '-',
          unit: 'EA',
          qty: 1,
          unitPrice: originalAmount,
          totalPrice: originalAmount,
          prevQty: prevAmt > 0 ? 1 : 0,
          prevAmt,
          currQty: currAmt > 0 ? 1 : 0,
          currAmt,
          totalQty: (prevAmt > 0 ? 1 : 0) + (currAmt > 0 ? 1 : 0),
          totalAmt,
          remainQty: remainAmt > 0 ? 1 : 0,
          remainAmt,
          remark,
        };
      });

      const totalContractQty = itemRows.reduce((sum, r) => sum + r.qty, 0);
      const totalContractAmt = itemRows.reduce((sum, r) => sum + r.totalPrice, 0);
      const sumPrevQty = itemRows.reduce((sum, r) => sum + r.prevQty, 0);
      const sumPrevAmt = itemRows.reduce((sum, r) => sum + r.prevAmt, 0);
      const sumCurrQty = itemRows.reduce((sum, r) => sum + r.currQty, 0);
      const sumCurrAmt = itemRows.reduce((sum, r) => sum + r.currAmt, 0);
      const sumTotalQty = itemRows.reduce((sum, r) => sum + r.totalQty, 0);
      const sumTotalAmt = itemRows.reduce((sum, r) => sum + r.totalAmt, 0);
      const sumRemainQty = itemRows.reduce((sum, r) => sum + r.remainQty, 0);
      const sumRemainAmt = itemRows.reduce((sum, r) => sum + r.remainAmt, 0);

      const format = (v: number) => v === 0 ? '-' : v.toLocaleString();
      const formatW = (v: number) => v === 0 ? '-' : `${v.toLocaleString()}원`;

      const content = `<html><head><meta charset='utf-8'><title>기성 산출서</title><style>body { font-family: Arial; padding: 20px; } table { border-collapse: collapse; width: 100%; margin-top: 20px; } th, td { border: 1px solid #000; padding: 6px; text-align: center; } th { background-color: #eef; } h2, h4 { text-align: center; margin: 0; padding: 10px; }</style></head><body><h2>${selectedYear}년 (${Number(selectedMonth)})월 기성 산출서</h2><h4>업체 및 현장명 : ${sale.company.name} ${sale.site.name}</h4><table><thead><tr><th rowspan='2'>명칭</th><th rowspan='2'>규격</th><th rowspan='2'>단위</th><th colspan='3'>계약</th><th colspan='2'>전회기성</th><th colspan='2'>금회기성</th><th colspan='2'>누계기성</th><th colspan='2'>잔여기성</th><th rowspan='2'>비고</th></tr><tr><th>수량</th><th>단가</th><th>금액</th><th>수량</th><th>금액</th><th>수량</th><th>금액</th><th>수량</th><th>금액</th><th>수량</th><th>금액</th></tr></thead><tbody><tr><td>당초계약분</td><td>-</td><td>EA</td><td>${format(totalContractQty)}</td><td>-</td><td>${formatW(totalContractAmt)}</td><td>${format(sumPrevQty)}</td><td>${formatW(sumPrevAmt)}</td><td>${format(sumCurrQty)}</td><td>${formatW(sumCurrAmt)}</td><td>${format(sumTotalQty)}</td><td>${formatW(sumTotalAmt)}</td><td>${format(sumRemainQty)}</td><td>${formatW(sumRemainAmt)}</td><td>${sumRemainAmt && totalContractAmt ? (Math.round((sumRemainAmt / totalContractAmt) * 10000) / 100) + '%' : '-'}</td></tr>${itemRows.map(r => `<tr><td>${r.name}</td><td>${r.spec}</td><td>${r.unit}</td><td>${format(r.qty)}</td><td>${formatW(r.unitPrice)}</td><td>${formatW(r.totalPrice)}</td><td>${format(r.prevQty)}</td><td>${formatW(r.prevAmt)}</td><td>${format(r.currQty)}</td><td>${formatW(r.currAmt)}</td><td>${format(r.totalQty)}</td><td>${formatW(r.totalAmt)}</td><td>${format(r.remainQty)}</td><td>${formatW(r.remainAmt)}</td><td>${r.remark}</td></tr>`).join('')}</tbody></table></body></html>`;

      const newWindow = window.open('', '_blank');
      if (newWindow) {
        newWindow.document.open();
        newWindow.document.write(content);
        newWindow.document.close();
      }
    } catch (err) {
      console.error('문서 생성 실패:', err);
    }
  };

  return (
    <Box p={4}>
      <Typography variant="h5" mb={2}>기성 청구 문서 생성</Typography>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <InputLabel>연도</InputLabel>
            <Select value={selectedYear} onChange={handleYearChange}>
              {years.map(year => <MenuItem key={year} value={year}>{year}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <InputLabel>월</InputLabel>
            <Select value={selectedMonth} onChange={handleMonthChange}>
              {months.map(month => <MenuItem key={month} value={month}>{month}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <Button variant="contained" fullWidth onClick={handleSearch} disabled={!selectedYear || !selectedMonth}>조회</Button>
        </Grid>
      </Grid>

      {items.length > 0 && (
        <Box mt={4}>
          <Typography variant="h6" gutterBottom>기성 청구 대상</Typography>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>업체명</TableCell>
                <TableCell>현장명</TableCell>
                <TableCell>항목</TableCell>
                <TableCell>규격</TableCell>
                <TableCell align="right">단가</TableCell>
                <TableCell align="center">문서 생성</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map(item => (
                <TableRow key={item.id}>
                  <TableCell>{item.company_name}</TableCell>
                  <TableCell>{item.site_name}</TableCell>
                  <TableCell>{item.item}</TableCell>
                  <TableCell>{item.specification || '-'}</TableCell>
                  <TableCell align="right">{formatAmount(item.amount)}</TableCell>
                  <TableCell align="center">
                    <Button variant="outlined" onClick={() => handleGenerate(item)}>생성</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
};

export default DocumentBilling;